import { useApiErrorHandler } from '@/account/hook/useApiErrorHandler';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { registration as registrationAPI } from '@/api';
import { type RegistrationTableRow } from '@/api/registration';
import { useAdminAuth } from '@/app/ProviderAdminAuth';
import { UiButton, UiHStack } from '@/lib/ui';
import { useDataTableOptions } from '@/registration/hook/useDataTableOptions';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { type DatatableSelectOptions } from '@/api/tenantClient';
import {
  MantineReactTable,
  type MRT_ColumnFiltersState,
  type MRT_PaginationState,
  type MRT_SortingState,
  useMantineReactTable,
  type MRT_ColumnDef
} from 'mantine-react-table';
import { useCallback, useMemo, type FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobalToast } from '@/lib/hook';

dayjs.extend(advancedFormat);

interface DatatableProps {
  data: RegistrationTableRow[]
  isLoading: boolean
  isError: boolean
  isFetching: boolean
  refetch: () => void
  filters: MRT_ColumnFiltersState
  setFilters: React.Dispatch<React.SetStateAction<MRT_ColumnFiltersState>>
  sorting: MRT_SortingState
  setSorting: React.Dispatch<React.SetStateAction<MRT_SortingState>>
  search: string
  setSearch: (searchText: string) => void
  pagination: MRT_PaginationState
  setPagination: React.Dispatch<React.SetStateAction<MRT_PaginationState>>
  rowCount: number
  delegateTypeSelectOptions: DatatableSelectOptions
  sessionSelectOptions: DatatableSelectOptions
  attendeeCategorySelectOptions: DatatableSelectOptions
}

const Datatable: FC<DatatableProps> = ({
  data, isLoading, isError, isFetching, refetch, filters, setFilters, sorting, setSorting, pagination, setPagination, search, setSearch,
  rowCount, delegateTypeSelectOptions, sessionSelectOptions, attendeeCategorySelectOptions
}) => {
  const navigate = useNavigate();
  const { showToast } = useGlobalToast();
  const { tenantCode } = useRegisterRoute();
  const { createTenantAdminApiRequest } = useTenantApi();
  const { adminAuth } = useAdminAuth();
  const { reportToGlobal } = useApiErrorHandler();
  const dataTableOptions = useDataTableOptions<RegistrationTableRow>();

  const { mutate: exportEmailAsCSV, isLoading: isExportCSVLoading } = useMutation<{}, Error>({
    mutationFn: async () => {
      return await registrationAPI.exportEmailAsCSV(createTenantAdminApiRequest)({
        model: 'Cancelled-Registration',
        email: adminAuth.user!.email
      });
    },
    onError: (error) => {
      reportToGlobal(error);
    }
  });

  const onExportEmailAsCSV = useCallback(() => {
    showToast.success('CSV email sent!');
    return exportEmailAsCSV();
  }, [exportEmailAsCSV]);

  const columns = useMemo<Array<MRT_ColumnDef<RegistrationTableRow>>>(
    () => {
      return [
        {
          accessorKey: 'event.name',
          header: 'Event'
        },
        {
          accessorKey: 'attendeeCategory.name',
          header: 'Attendee group',
          filterVariant: 'select',
          minSize: 300,
          mantineFilterSelectProps: {
            data: attendeeCategorySelectOptions
          }
        },
        {
          accessorKey: 'discountCode',
          header: 'Discount code'
        },
        {
          accessorKey: 'user.fullName',
          header: 'Name'
        },
        {
          accessorKey: 'user.email',
          header: 'Email'
        },
        {
          accessorKey: 'country',
          header: 'Country'
        },
        {
          accessorKey: 'delegateType.name',
          header: 'Registration type',
          filterVariant: 'select',
          mantineFilterSelectProps: {
            data: delegateTypeSelectOptions
          }
        },
        {
          accessorKey: 'session.name',
          header: 'Tickets',
          filterVariant: 'multi-select',
          mantineFilterSelectProps: {
            data: sessionSelectOptions
          }
        },
      ];
    },
    []
  );

  const handleClearFilters = () => {
    setFilters([]);
    setPagination({ pageIndex: 0, pageSize: 10 });
    setSearch('');
    setSorting([]);
  };

  const table = useMantineReactTable({
    columns,
    data,
    initialState: {
      showColumnFilters: true,
      showGlobalFilter: !!search,
    },
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    onColumnFiltersChange: setFilters,
    onSortingChange: setSorting,
    onGlobalFilterChange: setSearch,
    onPaginationChange: setPagination,
    rowCount,
    state: {
      columnFilters: filters,
      isLoading,
      showAlertBanner: isError,
      showProgressBars: isFetching,
      sorting,
      globalFilter: search,
      pagination
    },
    displayColumnDefOptions: { 'mrt-row-actions': { size: 96 } },
    enableRowActions: false,
    positionActionsColumn: 'last',
    globalFilterFn: 'contains',
    renderTopToolbarCustomActions: () => {
      return (
        <UiHStack
          spacing={8}
          flexGrow={1}
          justifyContent={'flex-end'}
          px={8}
        >
          <UiButton
            px={0}
            size={'sm'}
            variant={'ghost'}
            colorScheme={'primary'}
            onClick={onExportEmailAsCSV}
            isLoading={isExportCSVLoading}>
            Email as CSV
          </UiButton>
          <UiButton
            px={0}
            size={'sm'}
            variant={'ghost'}
            colorScheme={'primary'}
            onClick={handleClearFilters}>
            Clear all filters
          </UiButton>
          <UiButton
            px={0}
            size={'sm'}
            variant={'ghost'}
            colorScheme={'primary'}
            onClick={refetch}>
            Apply filter
          </UiButton>
        </UiHStack>
      );
    },
    ...dataTableOptions
  });

  return <MantineReactTable table={table} />;
};

export default Datatable;
